import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Send, Bell, AlertCircle } from 'lucide-react';
import {
    sendNotificationToAllUsers,
    sendNotificationToProviders,
    sendNotificationToProvidersByType,
} from '../../services/controlPanelApi';
import Spinner from '../Spinner/Spinner';
import './NotificationSender.css';

const TARGET_OPTIONS = [
    { value: 'all', label: 'Todos los usuarios', hint: 'Notificación general a la base completa' },
    { value: 'providers', label: 'Solo proveedores', hint: 'Cualquier proveedor activo o inactivo' },
    { value: 'specific_provider_type', label: 'Proveedores por tipo', hint: 'Filtrar por servicios o productos' },
];

const NotificationSender = () => {
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [redirectPath, setRedirectPath] = useState('');
    const [notificationType] = useState('admin_message');
    const [targetAudience, setTargetAudience] = useState('all');
    const [providerType, setProviderType] = useState('service');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!title.trim() || !body.trim()) {
            const msg = 'El título y el cuerpo son obligatorios.';
            setError(msg);
            toast.error(msg);
            return;
        }

        if (targetAudience === 'specific_provider_type' && !providerType.trim()) {
            const msg = 'El tipo de proveedor es obligatorio para este objetivo.';
            setError(msg);
            toast.error(msg);
            return;
        }

        setLoading(true);
        try {
            let response;
            if (targetAudience === 'all') {
                response = await sendNotificationToAllUsers(title, body, notificationType, redirectPath);
            } else if (targetAudience === 'providers') {
                response = await sendNotificationToProviders(title, body, notificationType, redirectPath);
            } else {
                response = await sendNotificationToProvidersByType(providerType, title, body, notificationType, redirectPath);
            }

            toast.success(response || 'Notificación enviada exitosamente');
            setTitle('');
            setBody('');
            setProviderType('service');
            setRedirectPath('');
            setTargetAudience('all');
        } catch (err) {
            console.error('Error al enviar notificación:', err);
            const apiMsg = err.response?.data || 'Error al enviar notificación.';
            const msg = typeof apiMsg === 'string' ? apiMsg : 'Error al enviar notificación.';
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cme-notif">
            <header className="cme-notif__header">
                <div className="cme-notif__title-wrap">
                    <div className="cme-notif__title-icon"><Bell size={20} /></div>
                    <div>
                        <h1 className="cme-notif__title">Enviar notificación push</h1>
                        <p className="cme-notif__subtitle">
                            Comunicá novedades a usuarios o proveedores. Solo texto + redirección opcional.
                        </p>
                    </div>
                </div>
            </header>

            {error && (
                <div className="cme-notif__alert" role="alert">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            <form className="cme-notif__form" onSubmit={handleSubmit}>
                {loading && <Spinner />}

                <div className="cme-notif__field">
                    <label className="cme-notif__label" htmlFor="cme-notif-title">Título</label>
                    <input
                        id="cme-notif-title"
                        type="text"
                        className="cme-notif__input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Ej: Nueva categoría disponible"
                        maxLength={120}
                        required
                    />
                    <span className="cme-notif__hint">{title.length}/120 caracteres</span>
                </div>

                <div className="cme-notif__field">
                    <label className="cme-notif__label" htmlFor="cme-notif-body">Cuerpo</label>
                    <textarea
                        id="cme-notif-body"
                        className="cme-notif__textarea"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Contenido de la notificación. Enter para saltos de línea."
                        rows={4}
                        required
                    />
                </div>

                <div className="cme-notif__field">
                    <label className="cme-notif__label">Audiencia</label>
                    <div className="cme-notif__audience-grid" role="radiogroup">
                        {TARGET_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                role="radio"
                                aria-checked={targetAudience === opt.value}
                                className={`cme-notif__audience-card ${targetAudience === opt.value ? 'is-active' : ''}`}
                                onClick={() => setTargetAudience(opt.value)}
                            >
                                <span className="cme-notif__audience-label">{opt.label}</span>
                                <span className="cme-notif__audience-hint">{opt.hint}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {targetAudience === 'specific_provider_type' && (
                    <div className="cme-notif__field">
                        <label className="cme-notif__label" htmlFor="cme-notif-provtype">Tipo de proveedor</label>
                        <select
                            id="cme-notif-provtype"
                            className="cme-notif__input"
                            value={providerType}
                            onChange={(e) => setProviderType(e.target.value)}
                            required
                        >
                            <option value="service">Servicios</option>
                            <option value="products">Productos</option>
                        </select>
                    </div>
                )}

                <div className="cme-notif__field">
                    <label className="cme-notif__label" htmlFor="cme-notif-redir">Redirección (opcional)</label>
                    <input
                        id="cme-notif-redir"
                        type="text"
                        className="cme-notif__input"
                        value={redirectPath}
                        onChange={(e) => setRedirectPath(e.target.value)}
                        placeholder="Ej: /profile, /services/some-id"
                    />
                    <span className="cme-notif__hint">
                        Path interno al que va el usuario al tocar la notificación.
                    </span>
                </div>

                <div className="cme-notif__actions">
                    <button
                        type="submit"
                        className="cme-notif__submit-btn"
                        disabled={loading}
                    >
                        <Send size={16} />
                        <span>{loading ? 'Enviando…' : 'Enviar notificación'}</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default NotificationSender;
